import {useEffect, useMemo, useState} from 'react';
import {useParams, useSearchParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {doc, getDoc} from 'firebase/firestore';
import {db} from '../firebase';
import css from './publicNote.module.css';
import noteCss from '../components/note/note.module.css';
import type {SharedNote} from '../types/Note';
import {Icon, type IconName} from '../components/common/Icon';
import {Button, Markdown, Spinner} from '../components/common/ui';
import {QuizTab} from '../components/note/QuizTab';
import {FlashcardsTab} from '../components/note/FlashcardsTab';
import {StoreBadges} from '../components/common/StoreBadges';
import {useToast} from '../context/ToastProvider';
import {languageName} from '../services/languages';
import owl from '../assets/owl.png';

type TabKey = 'summary' | 'transcript' | 'quiz' | 'flashcards';

const TAB_META: Array<{key: TabKey; icon: IconName; labelKey: string}> = [
  {key: 'summary', icon: 'sparkles', labelKey: 'Summary'},
  {key: 'transcript', icon: 'notes', labelKey: 'Transcription'},
  {key: 'quiz', icon: 'quiz', labelKey: 'Quiz'},
  {key: 'flashcards', icon: 'cards', labelKey: 'Flashcards'},
];

function CtaFooter() {
  const {t} = useTranslation();
  return (
    <div className={css.cta}>
      <h2 className={css.ctaTitle}>{t('WebSharedCtaTitle')}</h2>
      <p className={css.ctaSub}>{t('WebSharedCtaSub')}</p>
      <StoreBadges placement="public_note" />
      <a
        className={css.ctaWebLink}
        href="/?utm_source=note_share&utm_medium=referral&utm_campaign=public_note"
      >
        {t('WebSharedOpenApp')} →
      </a>
    </div>
  );
}

/**
 * Public, unauthenticated view of a shared note (shares/{shareId}).
 * Read-only: summary/transcript markdown plus quiz/flashcards from the
 * share doc — no callables, no audio.
 */
export function PublicNotePage() {
  const {shareId} = useParams<{shareId: string}>();
  const [searchParams] = useSearchParams();
  const {t} = useTranslation();
  const showToast = useToast();

  // undefined = loading, null = missing/error
  const [share, setShare] = useState<SharedNote | null | undefined>(undefined);

  useEffect(() => {
    if (!shareId) {
      setShare(null);
      return;
    }
    getDoc(doc(db, `shares/${shareId}`))
      .then(snap => setShare(snap.exists() ? (snap.data() as SharedNote) : null))
      .catch(e => {
        console.warn(e);
        setShare(null);
      });
  }, [shareId]);

  const tabs = useMemo(
    () =>
      share
        ? TAB_META.filter(item => {
            switch (item.key) {
              case 'summary':
                return !!share.summarize;
              case 'transcript':
                return !!share.content;
              case 'quiz':
                return !!share.quiz?.length;
              case 'flashcards':
                return !!share.flashcards?.length;
            }
          })
        : [],
    [share],
  );

  const requestedTab = searchParams.get('tab') as TabKey | null;
  const [tab, setTab] = useState<TabKey | null>(null);
  const activeTab =
    tab ??
    (requestedTab && tabs.some(item => item.key === requestedTab)
      ? requestedTab
      : tabs[0]?.key ?? null);

  if (share === undefined) {
    return (
      <div className={css.center}>
        <Spinner large />
      </div>
    );
  }

  const copyActive = async () => {
    const text =
      activeTab === 'summary' ? share?.summarize : share?.content;
    if (text) {
      await navigator.clipboard.writeText(text);
      showToast(
        t(activeTab === 'summary' ? 'SummaryCopied' : 'TranscriptCopied'),
        'success',
      );
    }
  };

  return (
    <div className={css.page}>
      <div className={css.inner}>
        <header className={css.brandBar}>
          <a className={css.brand} href="/">
            <img src={owl} alt="" className={css.brandOwl} />
            Owlly
          </a>
          <a
            className={css.openApp}
            href="/?utm_source=note_share&utm_medium=referral&utm_campaign=public_note"
          >
            {t('WebSharedOpenApp')}
          </a>
        </header>

        {share === null ? (
          <>
            <div className={css.notFound}>
              <img src={owl} alt="" className={css.notFoundOwl} />
              <h1 className={css.notFoundTitle}>{t('WebShareNotFoundTitle')}</h1>
              <p>{t('WebShareNotFoundBody')}</p>
            </div>
            <CtaFooter />
          </>
        ) : (
          <>
            <header className={noteCss.detailHeader}>
              <span className={noteCss.detailEmoji}>{share.emoji ?? '📝'}</span>
              <div className={noteCss.detailTitleWrap}>
                <h1 className={noteCss.detailTitle} style={{cursor: 'default'}}>
                  {share.title ?? t('Note_title')}
                </h1>
                <div className={noteCss.detailMeta}>
                  <span>{t('WebSharedWithOwlly')}</span>
                  {share.language && <span>{languageName(share.language)}</span>}
                </div>
              </div>
            </header>

            <nav className={noteCss.tabs}>
              {tabs.map(item => (
                <button
                  key={item.key}
                  className={
                    activeTab === item.key ? noteCss.tabActive : noteCss.tab
                  }
                  onClick={() => setTab(item.key)}
                  type="button"
                >
                  <span className={noteCss.tabInner}>
                    <Icon name={item.icon} size={15} />
                    {t(item.labelKey)}
                  </span>
                  <span
                    className={
                      activeTab === item.key
                        ? noteCss.tabIndicator
                        : noteCss.tabIndicatorGhost
                    }
                  />
                </button>
              ))}
            </nav>

            <div className={noteCss.tabBody}>
              {(activeTab === 'summary' || activeTab === 'transcript') && (
                <>
                  <div className={css.copyRow}>
                    <Button variant="secondary" icon="copy" onClick={copyActive}>
                      {t('Copy')}
                    </Button>
                  </div>
                  <Markdown>
                    {(activeTab === 'summary'
                      ? share.summarize
                      : share.content) ?? ''}
                  </Markdown>
                </>
              )}
              {activeTab === 'quiz' && (
                <QuizTab initialQuestions={share.quiz} />
              )}
              {activeTab === 'flashcards' && (
                <FlashcardsTab initialCards={share.flashcards} />
              )}
            </div>

            <CtaFooter />
          </>
        )}
      </div>
    </div>
  );
}
